import React, { useState } from "react";
import { Plus, Pencil, Loader2 } from "lucide-react";
import { SlideOver } from "@/components/SlideOver";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, apiError } from "@/lib/api";
import { CRATE_STATUSES } from "@/lib/constants";

const selectClass = "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
const textareaClass = "flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
const CARDBOARD = ["Good", "Damaged", "Missing"];

export function CrateForm({ clients, crate, onSaved }) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const empty = { crate_code: "", client_id: clients[0]?.id || "", has_cardboard_box: true, cardboard_condition: "Good", notes: "" };
  const [v, setV] = useState(crate ? {
    crate_code: crate.crate_code, client_id: crate.client_id,
    has_cardboard_box: !!crate.has_cardboard_box,
    cardboard_condition: crate.cardboard_condition || "Good",
    notes: crate.notes || "",
  } : empty);

  function set(k, val) { setV((p) => ({ ...p, [k]: val })); }

  async function submit(e) {
    e.preventDefault();
    setError(null); setSaving(true);
    try {
      if (crate) await api.put(`/crates/${crate.id}`, v);
      else await api.post("/crates", v);
      setOpen(false);
      if (!crate) setV(empty);
      onSaved && onSaved();
    } catch (err) {
      setError(apiError(err));
    } finally { setSaving(false); }
  }

  return (
    <>
      {crate ? (
        <Button variant="outline" size="sm" onClick={() => setOpen(true)}><Pencil className="h-4 w-4" /> Edit</Button>
      ) : (
        <Button onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> Add Crate</Button>
      )}
      <SlideOver open={open} onClose={() => setOpen(false)} title={crate ? `Edit ${crate.crate_code}` : "Add Wooden Crate"}>
        <form onSubmit={submit} className="space-y-4">
          {error && <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</p>}
          <div className="space-y-2">
            <Label htmlFor="crate_code">Crate ID *</Label>
            <Input id="crate_code" value={v.crate_code} onChange={(e) => set("crate_code", e.target.value)} placeholder="e.g. CRATE-0003" required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="client_id">Organization *</Label>
            <select id="client_id" className={selectClass} value={v.client_id} onChange={(e) => set("client_id", e.target.value)} required>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={v.has_cardboard_box} onChange={(e) => set("has_cardboard_box", e.target.checked)} className="h-4 w-4 rounded border-input" />
            Cardboard box inside the crate
          </label>
          {v.has_cardboard_box && (
            <div className="space-y-2">
              <Label htmlFor="cardboard_condition">Cardboard Condition</Label>
              <select id="cardboard_condition" className={selectClass} value={v.cardboard_condition} onChange={(e) => set("cardboard_condition", e.target.value)}>
                {CARDBOARD.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <textarea id="notes" rows={3} value={v.notes} onChange={(e) => set("notes", e.target.value)} className={textareaClass} />
          </div>
          <Button type="submit" className="w-full" disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {crate ? "Save Changes" : "Add Crate"}
          </Button>
        </form>
      </SlideOver>
    </>
  );
}

const CRATE_MANUAL_STATUSES = CRATE_STATUSES;

export function CrateStatusForm({ crateId, currentStatus, onSaved }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(CRATE_MANUAL_STATUSES.includes(currentStatus) ? currentStatus : "Returned to Warehouse");
  const [note, setNote] = useState("");
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setError(null); setSaving(true);
    try {
      await api.post(`/crates/${crateId}/status`, { status, note });
      setOpen(false); setNote("");
      onSaved && onSaved();
    } catch (err) { setError(apiError(err)); } finally { setSaving(false); }
  }

  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)}>Update Status</Button>
      <SlideOver open={open} onClose={() => setOpen(false)} title="Update Crate Status">
        <form onSubmit={submit} className="space-y-4">
          {error && <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</p>}
          <p className="text-sm text-muted-foreground">Current status: <span className="font-medium">{currentStatus}</span></p>
          <div className="space-y-2">
            <Label htmlFor="status">New Status</Label>
            <select id="status" className={selectClass} value={status} onChange={(e) => setStatus(e.target.value)}>
              {CRATE_MANUAL_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="note">Note (optional)</Label>
            <textarea id="note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} className={textareaClass} />
          </div>
          <Button type="submit" className="w-full" disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Update Status
          </Button>
        </form>
      </SlideOver>
    </>
  );
}
